import { useEffect, useRef, useState } from "react";
import type { AudioClipInstance } from "@/api/client";
import type { CommitClipsFn } from "@/hooks/useClipHistory";

// ─── Types ────────────────────────────────────────────────────────────────────

type Params = {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  audioRef:           React.RefObject<HTMLAudioElement | null>;
  pxPerSec:           number;
  storyDuration:      number;
  isPlaying:          boolean;
  setIsPlaying:       React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTime:     (t: number) => void;
  currentTimeRef:     React.MutableRefObject<number>;
  audioDurationRef:   React.MutableRefObject<number | null>;
  selectedClipIdRef:  React.MutableRefObject<string | null>;
  isGalleryOpen:      boolean;
  showImportModal:    boolean;
  audioSrc:           string | null;
  clipsRef:           React.MutableRefObject<AudioClipInstance[]>;
  patchClipLive:      (id: string, patch: Partial<AudioClipInstance>) => void;
  captureSnapshotRef: React.MutableRefObject<() => void>;
  commitClipsFnRef:   React.MutableRefObject<CommitClipsFn>;
  splitClipAt:        (id: string, t: number) => boolean;
  removeClip:         (id: string) => void;
  undo:               () => void;
  redo:               () => void;
  zoomIn:             () => void;
  zoomOut:            () => void;
  setToolMode:        React.Dispatch<React.SetStateAction<"select" | "cut">>;
  triggerUpdateStatus: () => void;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Timeline scrubbing (click/drag on the ruler to seek) and all keyboard
 * shortcuts (Space, Delete, Ctrl+Z/Y, arrow nudge, zoom, cut mode, etc.).
 */
export function useTimelineScrubbing({
  scrollContainerRef, audioRef, pxPerSec, storyDuration,
  isPlaying, setIsPlaying, setCurrentTime, currentTimeRef,
  audioDurationRef, selectedClipIdRef,
  isGalleryOpen, showImportModal, audioSrc,
  clipsRef, patchClipLive, captureSnapshotRef, commitClipsFnRef,
  splitClipAt, removeClip, undo, redo,
  zoomIn, zoomOut, setToolMode, triggerUpdateStatus,
}: Params) {
  const [isScrubbingTimeline, setIsScrubbingTimeline] = useState(false);
  const wasPlayingRef = useRef(false);

  // ── Scrubbing ────────────────────────────────────────────────────────────────

  const computeTimeFromScrollX = (clientX: number): number => {
    const scrollLeft = scrollContainerRef.current?.scrollLeft ?? 0;
    const rect       = scrollContainerRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const x = clientX - rect.left + scrollLeft;
    return Math.max(0, Math.min(storyDuration, x / pxPerSec));
  };

  const handleTimelineMouseDown = (e: React.MouseEvent) => {
    if (!audioSrc) return;
    wasPlayingRef.current = isPlaying;
    audioRef.current?.pause();
    setIsPlaying(false);
    setIsScrubbingTimeline(true);
    setCurrentTime(computeTimeFromScrollX(e.clientX));
  };

  const handleTimelineWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY !== 0 && e.deltaX === 0) {
      e.preventDefault();
      if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  useEffect(() => {
    if (!isScrubbingTimeline) return;
    const onMove = (e: MouseEvent) => setCurrentTime(computeTimeFromScrollX(e.clientX));
    const onUp   = (e: MouseEvent) => {
      const t = computeTimeFromScrollX(e.clientX);
      setCurrentTime(t);
      if (audioRef.current) audioRef.current.currentTime = t;
      setIsScrubbingTimeline(false);
      if (wasPlayingRef.current) {
        audioRef.current?.play().catch(() => {});
        setIsPlaying(true);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrubbingTimeline, storyDuration]);

  // ── Seek helper ──────────────────────────────────────────────────────────────

  const seekTo = (t: number) => {
    const max     = audioDurationRef.current ?? Infinity;
    const clamped = Math.max(0, Math.min(max, t));
    if (audioRef.current) audioRef.current.currentTime = clamped;
    currentTimeRef.current = clamped;
    setCurrentTime(clamped);
  };

  // ── Arrow-key clip nudge (debounced remix) ────────────────────────────────

  const arrowBurstActiveRef  = useRef(false);
  const arrowCommitTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  const moveSelectedClip = (deltaSec: number) => {
    const id = selectedClipIdRef.current;
    if (!id) return;
    const clip = clipsRef.current.find((c) => c.id === id);
    if (!clip) return;
    if (!arrowBurstActiveRef.current) {
      captureSnapshotRef.current();
      arrowBurstActiveRef.current = true;
    }
    patchClipLive(id, { start_seconds: Math.max(0, clip.start_seconds + deltaSec) });
    if (arrowCommitTimerRef.current) clearTimeout(arrowCommitTimerRef.current);
    arrowCommitTimerRef.current = setTimeout(() => {
      arrowBurstActiveRef.current = false;
      triggerUpdateStatus();
      commitClipsFnRef.current(clipsRef.current, { remix: true });
    }, 400);
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────

  // Use a stable ref so the handler always sees the latest closures without
  // re-registering the global listener.
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target?.matches?.("input, textarea, select, [contenteditable='true']")) return;
    if (isGalleryOpen || showImportModal) return;

    const ctrl  = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    const alt   = e.altKey;
    const key   = e.key;

    // Space — play / pause
    if (key === " " && !ctrl && !alt && !shift) {
      if (!audioSrc) return;
      e.preventDefault();
      setIsPlaying((v) => !v);
      return;
    }
    // Delete / Backspace — remove selected clip
    if ((key === "Delete" || key === "Backspace") && !ctrl) {
      const id = selectedClipIdRef.current;
      if (!id) return;
      e.preventDefault();
      removeClip(id);
      return;
    }
    // Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y — undo / redo
    if (ctrl && (key === "z" || key === "Z")) {
      e.preventDefault();
      if (shift) redo(); else undo();
      return;
    }
    if (ctrl && (key === "y" || key === "Y")) {
      e.preventDefault();
      redo();
      return;
    }
    // C / V — cut / select tool mode
    if (!ctrl && !alt && (key === "c" || key === "C")) { e.preventDefault(); setToolMode("cut");    return; }
    if (!ctrl && !alt && (key === "v" || key === "V")) { e.preventDefault(); setToolMode("select"); return; }
    // S — split selected clip at playhead
    if (!ctrl && !alt && (key === "s" || key === "S")) {
      const id = selectedClipIdRef.current;
      if (!id) return;
      e.preventDefault();
      splitClipAt(id, currentTimeRef.current);
      return;
    }
    // [ / ] — zoom
    if (key === "[" && !ctrl) { e.preventDefault(); zoomOut(); return; }
    if (key === "]" && !ctrl) { e.preventDefault(); zoomIn();  return; }
    // Home / End — jump playhead
    if (key === "Home") { e.preventDefault(); seekTo(0);                              return; }
    if (key === "End")  { e.preventDefault(); seekTo(audioDurationRef.current ?? 0);  return; }
    // Arrow keys — nudge clip or seek
    if (key === "ArrowLeft" || key === "ArrowRight") {
      const dir  = key === "ArrowLeft" ? -1 : 1;
      const step = alt ? 1 : shift ? 0.01 : 0.1;
      e.preventDefault();
      if (selectedClipIdRef.current) moveSelectedClip(dir * step);
      else seekTo(currentTimeRef.current + dir * step);
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => keyHandlerRef.current(e);
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      if (arrowCommitTimerRef.current) clearTimeout(arrowCommitTimerRef.current);
    };
  }, []);

  return { handleTimelineMouseDown, handleTimelineWheel, seekTo };
}
